import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, TrendingUp, TrendingDown, Minus, ArrowRight, Lightbulb } from "lucide-react";
import Panda from "./Panda";
import api from "../lib/api";
import { yearInsight } from "../lib/insight";

const METRICS = [
  { key: "active_days", label: "Active days" },
  { key: "bamboo_year", label: "Bamboo" },
  { key: "certificates_year", label: "Certificates" },
  { key: "longest_streak_weeks", label: "Longest streak", suffix: "w" },
  { key: "cheers_year", label: "Cheers" },
];

const Trend = ({ now, prev }) => {
  if (prev === undefined || prev === null || (now === 0 && prev === 0)) return null;
  if (now === prev) return <Minus size={12} className="text-gray-500" />;
  return now > prev ? <TrendingUp size={12} className="text-[#2e8b57]" /> : <TrendingDown size={12} className="text-[#e5356f]" />;
};

// Past years' report cards side by side per child, with growth arrows against the previous year.
const ReportCardWall = ({ children }) => {
  const [hist, setHist] = useState({});
  const ids = children.map((c) => c.id).join(",");

  useEffect(() => {
    if (!ids) return;
    Promise.all(ids.split(",").map((id) => api.get(`/children/${id}/report-card/history`).then((r) => [id, r.data]).catch(() => [id, null]))).then((pairs) => setHist(Object.fromEntries(pairs)));
  }, [ids]);

  if (!children.length) return null;
  return (
    <div className="mt-10 bg-white rounded-3xl shadow-md p-6 sm:p-8" data-testid="report-card-wall">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <span className="w-12 h-12 rounded-2xl bg-[#e9f9fd] text-[#1b8bc9] flex items-center justify-center flex-shrink-0"><FileText /></span>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-[#0c4a6e]" style={{ fontFamily: "Literata, serif" }}>Report card wall</h2>
          <p className="text-sm text-gray-600 mt-1">Every year side by side - watch the habits grow. Tap a year to open the full printable card.</p>
        </div>
      </div>
      <div className="mt-6 space-y-6">
        {children.map((c) => {
          const h = hist[c.id];
          const years = h?.years || [];
          return (
            <div key={c.id} data-testid={`report-card-wall-${c.id}`}>
              <div className="flex items-center gap-2 mb-3">
                <Panda size={34} accessory={c.panda.accessory} scarf={c.panda.scarf} />
                <p className="font-bold text-[#0c4a6e]">{c.panda.name}</p>
                <Link to={`/report-card/${c.id}`} className="ml-auto text-xs font-bold text-[#1b8bc9] hover:text-[#e5356f] inline-flex items-center gap-1" data-testid={`report-card-link-${c.id}`}>Open latest <ArrowRight size={12} /></Link>
              </div>
              {!h ? <p className="text-sm text-gray-400">Loading years...</p> : (
                <div className="flex gap-3 overflow-x-auto pb-2 snap-x">
                  {years.map((y, i) => {
                    const prev = years[i - 1];
                    return (
                      <Link key={y.year} to={`/report-card/${c.id}?year=${y.year}`} className="snap-start min-w-[190px] max-w-[230px] rounded-2xl border-2 border-[#c9ecf5] bg-[#f7fdfe] hover:border-[#1b8bc9] hover:-translate-y-0.5 transition-[transform,border-color] p-4" data-testid={`report-card-year-${c.id}-${y.year}`}>
                        <p className="text-2xl font-bold text-[#0c4a6e]" style={{ fontFamily: "Literata, serif" }}>{y.year}</p>
                        <ul className="mt-2 space-y-1">
                          {METRICS.map((m) => (
                            <li key={m.key} className="flex items-center justify-between text-xs">
                              <span className="text-gray-500">{m.label}</span>
                              <span className="font-bold text-[#0c4a6e] inline-flex items-center gap-1" data-testid={`rcw-${c.id}-${y.year}-${m.key}`}>{y[m.key]}{m.suffix || ""} <Trend now={y[m.key]} prev={prev?.[m.key]} /></span>
                            </li>
                          ))}
                          <li className="flex items-center justify-between text-xs"><span className="text-gray-500">Quiz avg (all-time)</span><span className="font-bold text-[#0c4a6e]">{y.quiz_average ?? "-"}</span></li>
                        </ul>
                        {y.goal_badge && <p className="mt-2 text-[11px] font-bold text-[#7d5fb2]">{y.goal_badge.label} goal badge</p>}
                        {prev && <p className="mt-2 text-[11px] leading-snug text-[#0c4a6e] bg-[#e9f9fd] rounded-lg px-2 py-1.5 inline-flex items-start gap-1" data-testid={`rcw-insight-${c.id}-${y.year}`}><Lightbulb size={11} className="mt-0.5 flex-shrink-0 text-[#f7931e]" /> {yearInsight(prev, y)}</p>}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReportCardWall;
